import { useState } from 'react'
import {
  Check,
  BarChart3,
  Brain,
  Target,
  ArrowRight,
  Calendar,
  Music,
  Mic2,
  ListTodo,
  Wallet,
  Handshake,
  Link2,
  Users,
  Settings,
  MapPinned,
  LayoutDashboard,
  Zap,
  FileSpreadsheet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

const DECK_PASSWORD = 'dizee2026'

const DECK_IMAGES = {
  whatsHappening: '/deck/cool/win.png',
  problem: '/deck/cool/stress.png',
  whyWins: '/deck/cool/DIZEE_Deck%20_Image_1.jpg',
}

export default function SyncfulDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(false) }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-base font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">Confidential — For intended recipients only.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white pb-0 mb-0" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-4 right-4 sm:top-6 sm:right-8 z-50">
        <span className="text-[9px] sm:text-[10px] font-medium text-zinc-800 uppercase tracking-widest">Confidential</span>
      </div>
      <div className="w-full max-w-[1920px] mx-auto mb-0 pb-0">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className="w-full flex flex-col border-b border-white/[0.04] min-h-[100dvh] sm:min-h-0 sm:h-[1080px]"
          >
            <div className="flex-1 flex flex-col justify-center w-full min-w-0 px-4 py-8 sm:px-8 sm:py-12 md:px-12 md:py-16 lg:px-20 lg:py-20 xl:px-24 xl:py-20">
              <Section />
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════════════════════════════ */

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-xs sm:text-sm font-bold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="mt-6 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The <span className="text-[#FF2300]">new operating system</span> and{' '}
        <span className="text-[#FF2300]">source of truth</span>
        {' '}for modern music careers.
      </p>
      <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-base sm:text-[17px] text-white">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-2 text-[14px] sm:text-[15px] font-medium text-[#FF2300]">Seed</span>
        <span>Spring 2026</span>
        <span>dizee.com</span>
      </div>
    </div>
  )
}

function SlideTeam() {
  const team = [
    {
      name: 'Ryan Thomas',
      role: 'CEO',
      image: '/deck/ryan.png',
      desc: 'Multiple Grammy nominations. Over a billion streams amassed. 15 years of experience in the music industry.',
    },
    {
      name: 'Edward Contreras',
      role: 'CTO',
      image: '/deck/edward.png',
      desc: '10+ years building music technology. Built platforms for Kanye and globally iconic artists.',
    },
    {
      name: 'Aaron P. Wong',
      role: 'Board Member & Strategic Advisor',
      image: '/deck/aaron.png',
      desc: 'Built one of the largest electric bike companies in the world (Super73). Musician and DJ.',
    },
    {
      name: 'Owais Ghaus',
      role: 'CMO',
      image: '/deck/owais.png',
      desc: 'Deep connections in the music industry. Studied by some of the biggest artists in the world.',
    },
    {
      name: 'Christopher Alba',
      role: 'Creative Director',
      image: '/deck/christopher.png',
      desc: '15 years of experience. Career directing for hundreds of artists and some of the biggest brands in entertainment.',
    },
  ]

  return (
    <div>
      <Tag>Team</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        The team building this.
      </h2>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
        {team.slice(0, 3).map((t, i) => (
          <div key={i} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 flex flex-col">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/[0.1] flex-shrink-0 bg-white/5">
                <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{t.name}</h3>
                <p className="text-sm sm:text-base text-[#FF2300] font-semibold mt-1">{t.role}</p>
              </div>
            </div>
            <p className="text-lg sm:text-xl text-zinc-400 leading-relaxed">{t.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8 lg:max-w-[66.666%] mx-auto">
        {team.slice(3).map((t, i) => (
          <div key={i} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 flex flex-col">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/[0.1] flex-shrink-0 bg-white/5">
                <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{t.name}</h3>
                <p className="text-sm sm:text-base text-[#FF2300] font-semibold mt-1">{t.role}</p>
              </div>
            </div>
            <p className="text-lg sm:text-xl text-zinc-400 leading-relaxed">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideWhatsHappening() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-w-0">
      <div className="min-w-0">
        <Tag>What&apos;s Happening Now</Tag>
        <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
          The music industry has <span className="text-[#FF2300]">shifted.</span>
        </h2>
        <p className="mt-5 text-xl sm:text-2xl text-zinc-400">Music is a multi-billion dollar global industry now run by independent teams.</p>
        <div className="mt-12 flex flex-col sm:flex-row items-stretch justify-center gap-6 sm:gap-10">
          <div className="rounded-2xl border border-[#FF2300]/25 bg-[#FF2300]/[0.08] px-10 py-7 text-center flex-1 max-w-[400px]">
            <p className="text-5xl sm:text-6xl font-bold text-[#FF2300]">~75M</p>
            <p className="text-base sm:text-lg text-zinc-400 mt-2">Active music creators worldwide</p>
          </div>
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.08] px-10 py-7 text-center flex-1 max-w-[400px]">
            <p className="text-5xl sm:text-6xl font-bold text-emerald-400">200M+</p>
            <p className="text-base sm:text-lg text-zinc-400 mt-2">Expected by 2030</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center min-h-[320px] lg:min-h-[420px] w-full">
        <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl aspect-[16/10]">
          <img src={DECK_IMAGES.whatsHappening} alt="Independent power rising" className="absolute inset-0 w-full h-full object-cover object-center" />
        </div>
      </div>
    </div>
  )
}

function SlideProblem() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-w-0">
      <div className="flex items-center justify-center min-h-[320px] lg:min-h-[420px] w-full order-2 lg:order-1">
        <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl aspect-[16/10] bg-white/[0.03]">
          <img src={DECK_IMAGES.problem} alt="Music work" className="absolute inset-0 w-full h-full object-cover object-center" />
        </div>
      </div>
      <div className="min-w-0 order-1 lg:order-2">
        <Tag>The Problem</Tag>
        <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.08]">
          Artists and their teams lack <span className="text-[#FF2300]">real-time visibility</span> into their career.
        </h2>
        <div className="mt-10 space-y-5">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7">
            <p className="text-xl font-bold text-[#FF2300] mb-2">No Centralized System</p>
            <p className="text-lg text-zinc-300 leading-relaxed">Fragmented tools and data force music teams to make critical decisions without the full picture.</p>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7">
            <p className="text-xl font-bold text-[#FF2300] mb-2">Missed Opportunities</p>
            <p className="text-lg text-zinc-300 leading-relaxed">By the time data is gathered and verified, opportunities are missed and communication breaks down.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideSolution() {
  return (
    <div>
      <Tag>The Solution</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.08]">
        <span className="text-[#FF2300]">DIZEE</span> centralizes the modern music career.
      </h2>
      <p className="mt-5 text-xl sm:text-2xl text-zinc-400">
        Into a single <span className="text-[#FF2300] font-semibold">operating system</span> and{' '}
        <span className="text-[#FF2300] font-semibold">source of truth.</span>
      </p>
      <div className="mt-12 grid sm:grid-cols-3 gap-8">
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8">
          <BarChart3 className="w-8 h-8 text-[#FF2300] mb-4" />
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Real-Time Clarity</h3>
          <p className="text-lg text-zinc-400 leading-relaxed">Unified view of releases, shows, finances, team — in one place.</p>
        </div>
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8">
          <Brain className="w-8 h-8 text-[#FF2300] mb-4" />
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Smarter Decisions</h3>
          <p className="text-lg text-zinc-400 leading-relaxed">AI surfaces opportunities as they happen so you can act, not react.</p>
        </div>
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8">
          <Target className="w-8 h-8 text-[#FF2300] mb-4" />
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">End-to-End Management</h3>
          <p className="text-lg text-zinc-400 leading-relaxed">Catalog, touring, team, projects — all connected.</p>
        </div>
      </div>
    </div>
  )
}

function SlideProduct() {
  return (
    <div className="flex flex-col">
      <Tag>Product</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        A unified platform that centralizes your career.
      </h2>
      <p className="mt-4 text-xl sm:text-2xl text-zinc-400">Dashboard, Music, Shows, Schedule, Teams, Tasks, AI Intelligence, 24/7 Sync.</p>
      <div className="mt-10 lg:mt-12 flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-4 lg:gap-5 w-full pb-10 lg:pb-10">
        <div className="relative flex flex-col items-center order-2 lg:order-1 lg:flex-initial lg:min-h-0">
          <div className="hidden lg:flex flex-1 min-h-0 flex-col justify-end">
            <img src="/deck/phone-mock.png" alt="DIZEE mobile" className="w-auto h-[320px] object-contain drop-shadow-2xl" />
          </div>
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <img src="/deck/phone-mock.png" alt="DIZEE mobile" className="w-auto h-[320px] object-contain drop-shadow-2xl" />
            <p className="text-sm text-zinc-500">Mobile · Always in sync</p>
          </div>
          <p className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full mt-2 text-sm text-zinc-500 whitespace-nowrap">Mobile · Always in sync</p>
        </div>
        <div className="w-full max-w-[85%] lg:max-w-[55%] flex justify-center order-1 lg:order-2">
          <img src="/deck/dizee-overview.png" alt="DIZEE overview" className="w-full h-auto object-contain drop-shadow-2xl" />
        </div>
      </div>
    </div>
  )
}

/* ── Glass card primitives (inspired by digest / tutorial visuals) ───── */

function GlassCard({ children, className = '', glow = false }: { children: React.ReactNode; className?: string; glow?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm ${glow ? 'relative isolate overflow-hidden' : ''} ${className}`}
      style={glow ? { boxShadow: '0 0 28px -10px rgba(255,35,0,0.12)' } : {}}
    >
      {children}
    </div>
  )
}

function MiniNavItem({ icon: Icon, label, color, active = false }: { icon: LucideIcon; label: string; color: string; active?: boolean }) {
  return (
    <div className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${active ? 'bg-white/[0.06]' : ''}`}>
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <span className={active ? 'text-white font-medium' : 'text-zinc-500'}>{label}</span>
    </div>
  )
}

function SlideProductShowcase() {
  const navPreview: Array<{ icon: LucideIcon; label: string; color: string }> = [
    { icon: LayoutDashboard, label: 'Dashboard', color: '#818cf8' },
    { icon: Calendar, label: 'Schedule', color: '#f59e0b' },
    { icon: Music, label: 'Music', color: '#1DB954' },
    { icon: Mic2, label: 'Shows', color: '#9333ea' },
    { icon: MapPinned, label: 'Touring', color: '#f97316' },
    { icon: ListTodo, label: 'Tasks', color: '#FF6B35' },
    { icon: Wallet, label: 'Money', color: '#10b981' },
    { icon: Handshake, label: 'Deals', color: '#ea580c' },
    { icon: Link2, label: 'Links', color: '#0ea5e9' },
    { icon: Users, label: 'Team', color: '#8b5cf6' },
    { icon: Settings, label: 'Settings', color: '#64748b' },
  ]

  const shows = [
    { venue: 'The Roxy Theatre', city: 'Los Angeles, CA', date: 'Apr 14', status: 'Confirmed', color: 'bg-emerald-500/20 text-emerald-400' },
    { venue: 'Bowery Ballroom', city: 'New York, NY', date: 'Apr 18', status: 'On Sale', color: 'bg-sky-500/20 text-sky-400' },
    { venue: 'Metro Chicago', city: 'Chicago, IL', date: 'Apr 22', status: 'Pending', color: 'bg-amber-500/20 text-amber-400' },
  ]

  const sheetRows = [
    ['Venue', 'Date', 'City', 'Status'],
    ['The Roxy', '04/14', 'Los Angeles', 'Confirmed'],
    ['Bowery', '04/18', 'New York', 'Hold'],
    ['Metro', '04/22', 'Chicago', 'Pending'],
    ['9:30 Club', '04/26', 'Washington', 'Confirmed'],
  ]

  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  const dates = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26]
  const highlightDates = [10, 14, 18, 22]

  return (
    <div>
      <Tag>Inside the Product</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        Everything your career needs. <span className="text-[#FF2300]">One place.</span>
      </h2>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-4 gap-6 items-stretch">
        {/* Panel 1 — Navigation / Workspace */}
        <div className="flex flex-col gap-4">
          <GlassCard className="p-5 flex-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-4 h-4 rounded-sm bg-[#FF2300]" />
              <BrandLogo className="h-2.5 w-auto text-white" />
            </div>
            <div className="space-y-0.5">
              {navPreview.map((n) => (
                <MiniNavItem key={n.label} icon={n.icon} label={n.label} color={n.color} active={n.label === 'Dashboard'} />
              ))}
            </div>
          </GlassCard>
          <GlassCard glow className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#FF2300]/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-[#FF2300]" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">Daily Digest</p>
                <p className="text-xs text-zinc-500">AI-powered daily briefing</p>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Panel 2 — Shows */}
        <div className="flex flex-col gap-4">
          <GlassCard glow className="p-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-white">12</p>
                <p className="text-xs text-zinc-500 mt-0.5">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">8</p>
                <p className="text-xs text-zinc-500 mt-0.5">Confirmed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">4</p>
                <p className="text-xs text-zinc-500 mt-0.5">Pending</p>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-5 flex-1">
            <div className="flex items-center gap-2.5 mb-4">
              <Mic2 className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">Upcoming Shows</span>
            </div>
            <div className="space-y-2.5">
              {shows.map((s) => (
                <div key={s.venue} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3.5 py-3">
                  <div>
                    <p className="text-sm text-white font-medium">{s.venue}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s.city} · {s.date}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${s.color}`}>{s.status}</span>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Panel 3 — Schedule */}
        <div className="flex flex-col gap-4">
          <GlassCard className="p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-white uppercase tracking-wider">April 2026</span>
              <Calendar className="w-4 h-4 text-zinc-500" />
            </div>
            <div className="grid grid-cols-7 gap-1.5 mb-2.5">
              {days.map((d, i) => (
                <span key={i} className="text-xs text-zinc-600 text-center font-medium">{d}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {dates.map((d) => (
                <div
                  key={d}
                  className={`text-xs text-center py-1.5 rounded-md ${
                    highlightDates.includes(d) ? 'bg-[#FF2300]/20 text-[#FF2300] font-semibold' : d === 12 ? 'bg-white/10 text-white font-semibold' : 'text-zinc-500'
                  }`}
                >
                  {d}
                </div>
              ))}
            </div>
          </GlassCard>
          <GlassCard className="p-5 flex-1">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-3">Upcoming Events</p>
            <div className="space-y-2">
              {[
                { color: 'bg-purple-500', label: 'Show — The Roxy, LA', time: 'Apr 10 · 8pm' },
                { color: 'bg-amber-500', label: 'Rehearsal — Studio B', time: 'Apr 12 · 2pm' },
                { color: 'bg-purple-500', label: 'Show — Bowery Ballroom', time: 'Apr 14 · 9pm' },
                { color: 'bg-sky-500', label: 'Press Interview', time: 'Apr 18 · 11am' },
              ].map((ev) => (
                <div key={ev.label} className="flex items-center gap-2.5 rounded-md bg-white/[0.04] px-3 py-2.5">
                  <div className={`w-1 h-6 rounded-full ${ev.color} shrink-0`} />
                  <div className="min-w-0">
                    <p className="text-xs text-zinc-300 truncate">{ev.label}</p>
                    <p className="text-[10px] text-zinc-600">{ev.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Panel 4 — Sheets Sync + Team */}
        <div className="flex flex-col gap-4">
          <GlassCard className="p-5 overflow-hidden flex-1">
            <div className="flex items-center gap-2.5 mb-4">
              <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-semibold text-white">Google Sheets Sync</span>
            </div>
            <div className="rounded-lg border border-white/[0.06] overflow-hidden">
              {sheetRows.map((row, ri) => (
                <div key={ri} className={`grid grid-cols-4 ${ri === 0 ? 'bg-white/[0.06]' : ri % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  {row.map((cell, ci) => (
                    <span
                      key={ci}
                      className={`px-2.5 py-2 text-[11px] border-r border-white/[0.04] last:border-r-0 ${
                        ri === 0 ? 'font-semibold text-zinc-400 uppercase tracking-wider' : 'text-zinc-500'
                      } ${cell === 'Confirmed' ? 'text-emerald-400' : cell === 'Hold' ? 'text-amber-400' : cell === 'Pending' ? 'text-sky-400' : ''}`}
                    >
                      {cell}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </GlassCard>
          <GlassCard glow className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">Live Sync Active</p>
                <p className="text-xs text-zinc-500">Sheet edits flow into DIZEE instantly</p>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-5 flex-1">
            <div className="flex items-center gap-2.5 mb-4">
              <Users className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">Your Team</span>
            </div>
            <div className="space-y-2">
              {[
                { name: 'Sarah Chen', role: 'Manager', initials: 'SC', color: 'bg-purple-500/30' },
                { name: 'Mike Torres', role: 'Tour Manager', initials: 'MT', color: 'bg-amber-500/30' },
                { name: 'Jordan Lee', role: 'Publicist', initials: 'JL', color: 'bg-sky-500/30' },
              ].map((m) => (
                <div key={m.name} className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-3 py-2.5">
                  <div className={`w-7 h-7 rounded-full ${m.color} flex items-center justify-center`}>
                    <span className="text-[10px] font-bold text-white">{m.initials}</span>
                  </div>
                  <div>
                    <p className="text-xs text-white font-medium">{m.name}</p>
                    <p className="text-[10px] text-zinc-500">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}

function SlideMarket() {
  return (
    <div>
      <Tag>Market</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        A large, underserved market <span className="text-[#FF2300]">that keeps growing.</span>
      </h2>
      <div className="mt-12 grid sm:grid-cols-2 gap-10">
        <div className="rounded-2xl border border-[#FF2300]/25 bg-[#FF2300]/5 p-8 sm:p-10">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">TAM</h3>
          <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-2">$700M – $1.2B</p>
          <p className="text-lg text-zinc-400 mt-3">Professional Independent Music Infrastructure</p>
        </div>
        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-8 sm:p-10">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">5-Year Target</h3>
          <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-2">$15M – $20M ARR</p>
          <p className="text-lg text-zinc-400 mt-3">8,000–15,000 paying accounts · Blended ~$100/mo ARPU</p>
        </div>
      </div>
    </div>
  )
}

function SlidePricing() {
  return (
    <div>
      <Tag>Pricing & Early Traction</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        Replacing a <span className="text-[#FF2300]">$500+/mo</span> tool stack.
      </h2>
      <p className="mt-5 text-xl sm:text-2xl text-zinc-400 max-w-3xl">
        Artists and teams currently spend $500+ per month stitching together fragmented tools. DIZEE puts everything into one platform starting at $45/mo.
      </p>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8">
          <p className="text-5xl sm:text-6xl font-bold text-[#FF2300]">$500+</p>
          <p className="text-lg text-zinc-400 mt-3">What artists currently pay per month across fragmented tools and features</p>
        </div>
        <div className="rounded-2xl border border-[#FF2300]/25 bg-[#FF2300]/[0.06] p-8">
          <p className="text-5xl sm:text-6xl font-bold text-white">$45–$125</p>
          <p className="text-lg text-zinc-400 mt-3">DIZEE replaces it all — one platform, one subscription</p>
        </div>
        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-8">
          <p className="text-5xl sm:text-6xl font-bold text-emerald-400">20 teams</p>
          <p className="text-lg text-zinc-400 mt-3">We already have access to onboard — representing ~200 users and ~$240K potential ARR off the bat</p>
        </div>
      </div>
    </div>
  )
}

function SlideGTM() {
  const phases = [
    { phase: 'Phase 1', title: 'Founder-Led Wedge', timeline: 'Months 0–6', target: '0 → 40 accounts' },
    { phase: 'Phase 2', title: 'Signal Amplification', timeline: 'Months 6–12', target: '40 → 200 accounts' },
    { phase: 'Phase 3', title: 'Scalable Acquisition', timeline: 'Months 12–24', target: '200 → 1,200+ paying customers' },
  ]
  return (
    <div>
      <Tag>Go-To-Market</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
        Go to market strategy.
      </h2>
      <div className="mt-14 flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-3">
        {phases.map((p, i) => (
          <div key={i} className="flex items-stretch gap-3 flex-1">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 flex-1">
              <span className="text-xl sm:text-2xl font-bold text-[#FF2300]">{p.phase}</span>
              <h3 className="text-xl font-bold text-white mt-3">{p.title}</h3>
              <div className="mt-4 space-y-1">
                <p className="text-base text-zinc-500">{p.timeline}</p>
                <p className="text-xl sm:text-2xl font-bold text-zinc-200">{p.target}</p>
              </div>
            </div>
            {i < phases.length - 1 && (
              <div className="hidden lg:flex items-center justify-center flex-shrink-0 px-1">
                <ArrowRight className="w-8 h-8 text-[#FF2300]/50" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideAdvantages() {
  const bullets = [
    'CEO with proven industry credibility — multiple Grammy nominations, 1B+ streams, active Billboard-charting label.',
    'CTO and engineering team have built music technology for iconic artists across the industry.',
    'Deep global relationships across artists, agencies, promoters, and labels.',
    'Rare hybrid team of music insiders and modern product builders.',
    'Capital-efficient, agile, and lean operating team based in Los Angeles.',
  ]
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-w-0">
      <div className="min-w-0">
        <Tag>Why DIZEE Wins</Tag>
        <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
          Why <span className="text-[#FF2300]">DIZEE</span> wins.
        </h2>
        <ul className="mt-10 space-y-5 max-w-3xl">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-4">
              <Check className="w-6 h-6 text-[#FF2300] shrink-0 mt-0.5" />
              <span className="text-lg sm:text-xl text-zinc-300 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-center min-h-[320px] lg:min-h-[420px]">
        <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl aspect-[16/10]">
          <img src={DECK_IMAGES.whyWins} alt="Why DIZEE wins" className="absolute inset-0 w-full h-full object-cover object-center" />
        </div>
      </div>
    </div>
  )
}

function SlideAsk() {
  return (
    <div>
      <Tag>The Ask</Tag>
      <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tighter text-white leading-tight">
        Raising{' '}
        <span className="relative inline-block">
          <span className="relative z-10 text-[#FF2300]">$2.5M</span>
          <span className="absolute inset-0 bg-[#FF2300]/15 blur-xl rounded-full -z-0" aria-hidden />
        </span>
        {' '}to accelerate.
      </h2>
      <p className="mt-5 text-xl sm:text-2xl font-medium">
        <span className="text-[#FF2300]">Seed</span>
        <span className="text-zinc-400"> · Use of funds</span>
      </p>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {[
          'Product-market fit with real artists and managers',
          'Core platform development and system hardening',
          'Onboarding marquee artists and management partners',
          'Founding team focus and execution',
          'Beta expansion and early adoption',
          'Secure, scalable infrastructure',
        ].map((label, i) => (
          <div key={i} className="flex items-start gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-6 py-5">
            <span className="text-[#FF2300] shrink-0 mt-0.5 text-xl font-bold" aria-hidden>→</span>
            <p className="text-lg text-zinc-300 leading-snug">{label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The new operating system and source of truth for modern music careers.
      </p>
      <div className="mt-12 space-y-2">
        <p className="text-[15px] sm:text-[16px] text-[#FF2300] font-medium">invest@dizee.com</p>
        <p className="text-base sm:text-[17px] text-white">
          <span className="text-[#FF2300] font-medium">Seed</span>
          {' · Spring 2026'}
        </p>
        <p className="text-base sm:text-[17px] text-white">dizee.com</p>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   SLIDE ORDER
   ═══════════════════════════════════════════════════════════════════ */

const SLIDES: { title: string; component: React.FC }[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Team', component: SlideTeam },
  { title: "What's Happening", component: SlideWhatsHappening },
  { title: 'Problem', component: SlideProblem },
  { title: 'Solution', component: SlideSolution },
  { title: 'Product', component: SlideProduct },
  { title: 'Product Showcase', component: SlideProductShowcase },
  { title: 'Market', component: SlideMarket },
  { title: 'Pricing', component: SlidePricing },
  { title: 'Why DIZEE Wins', component: SlideAdvantages },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing', component: SlideClosing },
]
